"use client";

import { useText } from "@/app/(site)/europe/useTranslations";
import { useCallback, useEffect, useRef, useState } from "react";
import css from "./mdr-section.module.scss";
import { useMdrRecaptcha } from "./useMdrRecaptcha";

/**
 * The MDR tile's lead form: Zoho's own "MDR Form" (web form 3956778000199029001),
 * rebuilt in the tile's styling. Name, Email and Phone are its three fields;
 * LEADCF9 is a hidden Description field that labels the lead "MDR request" in
 * Zoho. The hidden tokens below (xnQsjsdp, xmIwtLD, and the analytics script's rid/tw) come
 * from that form's embed code and tie each post to it in Zoho. Zoho can issue new
 * ones when the form is re-published, so copy all of them from every new export
 * (last: 2 Oct 2026, the export that added LEADCF9). The returnURL's ?source=eu-mdr is ours, keep it.
 *
 * The captcha runs on the site-wide NEXT_PUBLIC_G_RC_SITE_KEY, like every other
 * form here. The key in Zoho's embed code (6Lf_Heof…) is registered to Zoho's own
 * domains: on active710.com and the pages.dev previews it only draws "Invalid
 * domain for site key", so it must not be copied back in on a re-export. For the
 * same reason reCAPTCHA must be switched OFF on the form in Zoho, as it is on the
 * contact form's: with it on, Zoho checks our token against its own key and drops
 * the lead, yet still redirects to returnURL, so the page looks like it worked.
 * It was switched off on 28 Sep 2026; if a re-export shows a g-recaptcha block
 * again, it has been switched back on.
 *
 * The widget is drawn with `grecaptcha.render` rather than a `.g-recaptcha`
 * class because the page's contact form further down has a `.g-recaptcha` of
 * its own. useMdrRecaptcha loads api.js without touching that form's loader.
 */

const FORM_ID = "3956778000199029001";
const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_G_RC_SITE_KEY ?? "";

declare global {
  interface Window {
    _wFa_ajax_will_be_replaced?: boolean;
    _wfa_fstprtcken?: Record<string, boolean>;
  }
}

export const MdrLeadForm = () => {
  // Flags the embed's inline script sets for Zoho's v2 analytics script
  // (wf_anal below), which reads them when it tracks this form.
  useEffect(() => {
    window._wFa_ajax_will_be_replaced = false;
    window._wfa_fstprtcken = { ...window._wfa_fstprtcken, [FORM_ID]: true };
  }, []);

  const nameLabel = useText("mdr.name");
  const emailLabel = useText("mdr.email");
  const phoneLabel = useText("mdr.phone");
  const buttonLabel = useText("mdr.button");
  const captchaError = useText("mdr.captcha-error");
  const disclaimer = useText("mdr.disclaimer");

  const widgetRef = useRef<HTMLDivElement>(null);
  const widgetId = useRef<number | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [showCaptchaError, setShowCaptchaError] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const containerRef = useMdrRecaptcha((api) => {
    const el = widgetRef.current;
    if (!el || widgetId.current !== null) return;
    widgetId.current = api.render(el, {
      sitekey: RECAPTCHA_SITE_KEY,
      theme: "dark",
      callback: (token) => {
        setCaptchaToken(token);
        setShowCaptchaError(false);
      },
      "expired-callback": () => setCaptchaToken(""),
    });
  });

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const form = event.currentTarget;

      // Zoho's honeypot: hidden from people, so only a bot fills it.
      const trap = form.elements.namedItem("aG9uZXlwb3Q") as HTMLInputElement;
      if (trap?.value) return;

      if (!captchaToken) {
        setShowCaptchaError(true);
        return;
      }

      setSubmitting(true);
      form.submit();
    },
    [captchaToken],
  );

  return (
    <div ref={containerRef}>
      <form
        id={`webform${FORM_ID}`}
        action="https://crm.zoho.com/crm/WebToLeadForm"
        name={`WebToLeads${FORM_ID}`}
        method="POST"
        acceptCharset="UTF-8"
        onSubmit={handleSubmit}
        className={css["form"]}
      >
        <input
          type="text"
          style={{ display: "none" }}
          name="xnQsjsdp"
          defaultValue="afe1d22537b0f093e9b6129504a9a728b2bb5e5b17f79fd798f5230ed8cc0b3b"
        />
        <input type="hidden" name="zc_gad" defaultValue="" />
        <input
          type="text"
          style={{ display: "none" }}
          name="xmIwtLD"
          defaultValue="69b05dafb0901c5aa94aa9b431f6b5f0dd46a67f8e803cd11a68e17c1572a9ffab6971dd35fde17fd8950a509074b627"
        />
        <input type="text" style={{ display: "none" }} name="actionType" defaultValue="TGVhZHM=" />
        <input
          type="text"
          style={{ display: "none" }}
          name="returnURL"
          defaultValue="https://active710.com/thankyou-mdr?source=eu-mdr"
        />
        <input type="hidden" name="LEADCF9" maxLength={255} defaultValue="MDR request" />

        <input
          type="text"
          name="aG9uZXlwb3Q"
          className={css["trap"]}
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          defaultValue=""
        />

        <label className={css["field"]}>
          <span className="sr-only">{nameLabel}</span>
          <input
            type="text"
            name="Last Name"
            maxLength={80}
            required
            autoComplete="name"
            placeholder={`${nameLabel} *`}
          />
        </label>

        <label className={css["field"]}>
          <span className="sr-only">{emailLabel}</span>
          <input
            type="email"
            ftype="email"
            name="Email"
            maxLength={100}
            required
            autoComplete="email"
            placeholder={`${emailLabel} *`}
          />
        </label>

        <label className={css["field"]}>
          <span className="sr-only">{phoneLabel}</span>
          <input
            type="tel"
            name="Phone"
            maxLength={30}
            required
            autoComplete="tel"
            placeholder={`${phoneLabel} *`}
          />
        </label>

        <div className={css["captcha"]}>
          <div ref={widgetRef} />
          {showCaptchaError && (
            <p className={css["captcha-error"]} role="alert">
              {captchaError}
            </p>
          )}
        </div>

        <button type="submit" className={css["submit"]} disabled={submitting}>
          <span>{buttonLabel}</span>
        </button>

        <p
          className={css["disclaimer"]}
          dangerouslySetInnerHTML={{ __html: disclaimer }}
        />

        {/* Do not remove this --- Analytics Tracking code starts */}
        <script
          async
          id="wf_anal"
          src="https://crm.zohopublic.com/crm/WebFormAnalyticsServeServlet?rid=9ceb4dc9f8b42d4c6357bfb3d6369a9c232960d832f98d3ef81efe9883042e6696ed7c14c333333caccdc9a944b1b1e7gid12a0a795b6d7b1c6726bc357e4e5df63d6ae776e68bdc765258531a1b060b1bagidbf51b86f6fd2ee68590d83137e171d0e6b0365a51ddab4f60a3eeceb88bd53cfgidded1386769c6f27d20dc9dec71a84746c51e221a1b3743c3fd5b221f4e797fc9&tw=3b2f8536d06a476a39740c99223461265ec00871f9705b7fc306e643ad10b06e&version=v2"
        />
        {/* Do not remove this --- Analytics Tracking code ends. */}
      </form>
    </div>
  );
};
